import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { dynamicTable } from '@/integrations/supabase/dynamicTable';
import { formatOrganizationCurrency } from '@/lib/currency';
import { captureException } from '@/lib/sentry';
import type { EntityCellConfig, EntityTableConfig, EntityFormatterContext } from '@/components/ui/editable-table/types';

export interface EntityOption {
  value: string;
  label: string;
}

/**
 * Fetch organization currency for formatting context
 */
async function fetchOrganizationCurrency(organizationId: string): Promise<EntityFormatterContext> {
  try {
    const { data, error } = await supabase
      .from('organizations')
      .select('currency')
      .eq('id', organizationId)
      .single();

    if (error) {
      // `error` is a PostgrestError, which extends Error — pass it straight through.
      captureException(error, {
        context: 'EntityOptions',
        action: 'fetchOrganizationCurrency',
        organizationId,
      });
      return {};
    }

    return {
      currencyCode: data?.currency || 'EUR',
    };
  } catch (err) {
    captureException(err instanceof Error ? err : new Error(String(err)), {
      context: 'EntityOptions',
      action: 'fetchOrganizationCurrency',
      organizationId,
    });
    return {};
  }
}

/**
 * Format currency using organization's currency code
 * Uses the same formatOrganizationCurrency that works everywhere else in the app
 */
function formatCurrency(amount: number, currencyCode?: string): string {
  // Construct organization-like object for formatOrganizationCurrency
  const org = currencyCode ? { currency: currencyCode } : null;
  return formatOrganizationCurrency(amount, org);
}

/**
 * Fetch options from a single table
 */
async function fetchTableOptions(
  tableConfig: EntityTableConfig,
  organizationId?: string,
  context?: EntityFormatterContext
): Promise<EntityOption[]> {
  try {
    // Use custom select if provided, otherwise select all columns
    const selectQuery = tableConfig.select || '*';
    // Dynamic (runtime-string) table + dynamic select string — route through the
    // generic helper, typing rows as a display-string bag (values are rendered as
    // option labels/values). The literal `from('organizations')` above stays typed.
    let query = dynamicTable<Record<string, string>>(tableConfig.table).select(selectQuery);

    // Apply organization filter if enabled (default true).
    // For dual-scope taxonomy tables, `includeGlobalScope` broadens the filter
    // to also return seeded rows where `organization_id IS NULL` — matches the
    // dual-scope read the `/api/v1/dual-scope-types` route does.
    if (tableConfig.filterByOrg !== false && organizationId) {
      if (tableConfig.includeGlobalScope) {
        query = query.or(`organization_id.is.null,organization_id.eq.${organizationId}`);
      } else {
        query = query.eq('organization_id', organizationId);
      }
    }

    // Apply additional filters
    if (tableConfig.additionalFilters) {
      Object.entries(tableConfig.additionalFilters).forEach(([key, value]) => {
        query = query.eq(key, value);
      });
    }

    const { data, error } = await query;

    if (error) {
      captureException(error, {
        context: 'EntityOptions',
        action: 'fetchTableOptions',
        table: tableConfig.table,
      });
      return [];
    }

    // Transform to value/label pairs
    return (data || []).map(row => {
      let label: string;
      if (tableConfig.displayFormatter) {
        label = tableConfig.displayFormatter(row, context);
      } else if (tableConfig.displayField.includes(',')) {
        // Handle multiple fields (e.g., 'first_name,last_name')
        const fields = tableConfig.displayField.split(',').map(f => f.trim());
        label = fields.map(f => row[f]).filter(Boolean).join(' ');
      } else {
        label = row[tableConfig.displayField] || '';
      }

      // Add prefixes if configured
      const valuePrefix = tableConfig.valuePrefix || '';
      const labelPrefix = tableConfig.labelPrefix || '';

      return {
        value: valuePrefix + row[tableConfig.valueField],
        label: labelPrefix ? `${labelPrefix} ${label}` : label,
      };
    });
  } catch (err) {
    captureException(err instanceof Error ? err : new Error(String(err)), {
      context: 'EntityOptions',
      action: 'fetchTableOptions',
      table: tableConfig.table,
    });
    return [];
  }
}

// Export formatCurrency for use in displayFormatters
export { formatCurrency };

/**
 * Fetch entity options from Supabase (single or multiple tables)
 */
async function fetchEntityOptions(
  config: EntityCellConfig,
  organizationId?: string
): Promise<EntityOption[]> {
  // Fetch organization currency for formatting context
  let context: EntityFormatterContext = {};
  if (organizationId) {
    context = await fetchOrganizationCurrency(organizationId);
  }

  // Handle multi-table configuration
  if (config.tables && config.tables.length > 0) {
    const allOptions = await Promise.all(
      config.tables.map(tableConfig => fetchTableOptions(tableConfig, organizationId, context))
    );
    return allOptions.flat();
  }

  // Single table configuration
  return fetchTableOptions(config, organizationId, context);
}

/**
 * Generate a stable query key for the entity options
 */
function getQueryKey(config: EntityCellConfig, organizationId?: string): string[] {
  if (config.tables && config.tables.length > 0) {
    // Multi-table config — include per-table scope so globals-on vs globals-off
    // variants don't collide in cache.
    const tablesKey = config.tables
      .map(t => `${t.table}:${t.valuePrefix || ''}:${t.includeGlobalScope ? 'g' : ''}`)
      .join('|');
    return ['entityOptions', 'multi', tablesKey, organizationId || 'all'];
  }
  // Single table config
  return [
    'entityOptions',
    config.table,
    organizationId || 'all',
    config.includeGlobalScope ? 'global+org' : 'org',
    JSON.stringify(config.additionalFilters || {}),
  ];
}

/**
 * React Query hook to fetch entity options for dropdown cells
 *
 * Uses React Query for:
 * - Automatic caching (5 min stale time)
 * - Request deduplication
 * - Cache invalidation on mutations
 */
export function useEntityOptions(
  config: EntityCellConfig | undefined,
  organizationId?: string
) {
  return useQuery({
    queryKey: config ? getQueryKey(config, organizationId) : ['entityOptions', 'disabled'],
    queryFn: () => {
      if (!config) return [];
      return fetchEntityOptions(config, organizationId);
    },
    enabled: !!config,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (garbage collection)
  });
}

/**
 * Hook to fetch options for multiple entity configs at once
 * Returns a map of config key -> options
 */
export function useMultipleEntityOptions(
  configs: Array<{ key: string; config: EntityCellConfig }>,
  organizationId?: string
) {
  // Create a unique key for this combination of configs
  const configsKey = configs.map(c => `${c.key}:${getQueryKey(c.config, organizationId).join(':')}`).join('|');

  return useQuery({
    queryKey: ['entityOptions', 'multiple', configsKey, organizationId || 'all'],
    queryFn: async () => {
      const results = await Promise.all(
        configs.map(async ({ key, config }) => ({
          key,
          options: await fetchEntityOptions(config, organizationId),
        }))
      );

      return results.reduce((acc, { key, options }) => {
        acc[key] = options;
        return acc;
      }, {} as Record<string, EntityOption[]>);
    },
    enabled: configs.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}